import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';

import { ROUTES } from '@/constants/routes';
import { Alert } from '@/design-system/components/Alert';
import { Loader } from '@/design-system/components/Loader';
import { toAppError } from '@/domain/errors';
import { AuthenticationLayout } from '@/layouts/AuthenticationLayout';
import { authenticationService } from '@/services/authentication-service';

interface VerifyEmailSearch {
  token: string;
}

function VerifyEmailChangePage() {
  const { token } = Route.useSearch();

  const verification = useQuery({
    queryKey: ['admin-email-change-verify', token],
    queryFn: async () => {
      await authenticationService.verifyEmailChange(token);
      return true as const;
    },
    enabled: token.trim() !== '',
    retry: false,
    refetchOnWindowFocus: false,
  });

  const missingToken = token.trim() === '';
  const appError =
    verification.error === null || verification.error === undefined
      ? null
      : toAppError(verification.error);

  return (
    <AuthenticationLayout
      title="Confirm Email"
      description="Completing your administrator email change."
    >
      {missingToken ? (
        <div className="space-y-4">
          <Alert severity="error" title="Verification link is not valid">
            This link is missing its token. Open the link from your email, or request a new one from
            Edit Profile.
          </Alert>
          <Link
            to={ROUTES.signIn}
            className="block text-center text-caption font-medium text-brand hover:underline"
          >
            Back to Sign In
          </Link>
        </div>
      ) : verification.isPending ? (
        <Loader className="py-8" label="Confirming your new email…" showLabel />
      ) : verification.isSuccess ? (
        <div className="space-y-4">
          <Alert severity="success" title="Email updated">
            Your login email has been changed. All sessions were signed out — sign in again with the
            new address.
          </Alert>
          <Link
            to={ROUTES.signIn}
            className="block text-center text-caption font-medium text-brand hover:underline"
          >
            Sign In
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          <Alert severity="error" title={appError?.title ?? 'Verification failed'}>
            {appError?.message ??
              'This verification link cannot be used. Request a new one from Edit Profile.'}
          </Alert>
          <Link
            to={ROUTES.signIn}
            className="block text-center text-caption font-medium text-brand hover:underline"
          >
            Back to Sign In
          </Link>
        </div>
      )}
    </AuthenticationLayout>
  );
}

export const Route = createFileRoute('/admin/verify-email')({
  validateSearch: (search: Record<string, unknown>): VerifyEmailSearch => ({
    token: typeof search.token === 'string' ? search.token : '',
  }),
  component: VerifyEmailChangePage,
});
